/// <reference lib="dom" />
import type { DesignDocument, TextLayer } from '../document/types.ts'
import { BASE_CSS } from '../style/baseCss.ts'
import { rootStyle, styleMapToCss, layerStyle, textStyle } from '../style/layerStyle.ts'

/**
 * Auto-Fit (shrink): kleinste Schriftgröße >= minPt, bei der der Text in seinen Rahmen passt,
 * gemessen mit dem echten DOM in einem unsichtbaren `.dr-root` bei gleichem --mm.
 * Das Ergebnis wird als `fittedSizePt` ins Layer geschrieben; der Druck misst nie nach.
 * Nur im Browser (Entry `@pfe/design-renderer/dom`).
 */
export function measureFittedSizePt(doc: DesignDocument, layer: TextLayer, pxPerMm: number, minPt = 6): number {
  if (typeof document === 'undefined') return layer.font.sizePt
  const host = document.createElement('div')
  host.style.cssText = 'position:absolute;left:-100000px;top:0;visibility:hidden;pointer-events:none;'
  const style = document.createElement('style')
  style.textContent = BASE_CSS
  host.appendChild(style)
  const root = document.createElement('div')
  root.className = 'dr-root dr-root--edit'
  root.style.cssText = styleMapToCss(rootStyle(doc.canvas, pxPerMm))
  const wrap = document.createElement('div')
  wrap.className = 'dr-layer dr-layer--text'
  wrap.style.cssText = styleMapToCss(layerStyle({ ...layer, frame: { ...layer.frame, rotationDeg: 0 } }, doc.canvas))
  const text = document.createElement('div')
  text.className = 'dr-text'
  const span = document.createElement('span')
  span.textContent = layer.text
  text.appendChild(span)
  wrap.appendChild(text)
  root.appendChild(wrap)
  host.appendChild(root)
  document.body.appendChild(host)

  const fits = (sizePt: number) => {
    text.style.cssText = styleMapToCss(textStyle({ ...layer, autoFit: 'shrink', fittedSizePt: sizePt }))
    return span.scrollHeight <= text.clientHeight + 0.5 && span.scrollWidth <= text.clientWidth + 0.5
  }

  try {
    let lo = minPt
    let hi = layer.font.sizePt
    if (fits(hi)) return hi
    if (!fits(lo)) return lo
    // Binärsuche in 0.25-pt-Schritten
    for (let i = 0; i < 12 && hi - lo > 0.25; i++) {
      const mid = (lo + hi) / 2
      if (fits(mid)) lo = mid
      else hi = mid
    }
    return Math.round(lo * 4) / 4
  } finally {
    host.remove()
  }
}

/** Text-Layer, deren Inhalt bei autoFit=none überläuft (für TEXT_OVERFLOW). */
export function measureOverflowingTextLayers(doc: DesignDocument, pxPerMm: number): string[] {
  if (typeof document === 'undefined') return []
  const out: string[] = []
  for (const layer of doc.layers) {
    if (layer.type !== 'text' || layer.autoFit !== 'none') continue
    const fitted = measureFittedSizePt(doc, { ...layer, autoFit: 'shrink' }, pxPerMm, 1)
    if (fitted < layer.font.sizePt - 0.01) out.push(layer.id)
  }
  return out
}
